/// <amd-module name="Wit/MentionsAutocomplete/Providers/WorkItemProviderCache" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { WorkItem, WorkItemErrorPolicy } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";

import * as Telemetry from "../Utilities/Telemetry";
import { ArtifactsCache } from "./ArtifactsCache";
import { IWorkItemProviderItem } from "./IWorkItemProviderItem";
import { Deferred, IDictionaryNumberTo } from "./Utils";

export class WorkItemProviderCache {
    private _client: IWorkItemTrackingRestClient;
    private _context: IVssPageContext;

    public cache: ArtifactsCache<IWorkItemProviderItem>;

    constructor(context: IVssPageContext, client: IWorkItemTrackingRestClient, queueWaitMSec: number, maxBatchSize: number) {
        this.cache = new ArtifactsCache<IWorkItemProviderItem>(keys => this._loadWorkItems(keys), queueWaitMSec, maxBatchSize);
        this._client = client;
        this._context = context;
    }

    private _loadWorkItems(ids: number[]): IDictionaryNumberTo<Promise<IWorkItemProviderItem>> {
        const fieldsToPull = [
            CoreFieldRefNames.Id,
            CoreFieldRefNames.WorkItemType,
            CoreFieldRefNames.Title,
            CoreFieldRefNames.TeamProject,
            CoreFieldRefNames.State
        ];
        const deferreds: IDictionaryNumberTo<Deferred<IWorkItemProviderItem>> = {};
        const promises: IDictionaryNumberTo<Promise<IWorkItemProviderItem>> = {};
        ids.forEach(id => {
            deferreds[id] = new Deferred<IWorkItemProviderItem>();
            promises[id] = deferreds[id].promise;
        });

        // We are using current team project name to use project-scoped REST API for public project
        // Note that project name is ignored on the server for members and work items that are not in the
        // current project will not be fetched for anonymous/public users
        const projectName = this._getProjectName();
        this._client.getWorkItems(ids, projectName, fieldsToPull, undefined, undefined, WorkItemErrorPolicy.Omit).then(
            (workItems: WorkItem[]) => {
                const workItemIdsFound: number[] = [];

                workItems
                    .filter(value => value !== null)
                    .map(workItem => {
                        const fields = workItem.fields;
                        const workItemType = fields[CoreFieldRefNames.WorkItemType];
                        const projectName = fields[CoreFieldRefNames.TeamProject];
                        const id = fields[CoreFieldRefNames.Id];
                        const state = fields[CoreFieldRefNames.State];

                        const result: IWorkItemProviderItem = {
                            id,
                            projectName,
                            workItemType,
                            title: fields[CoreFieldRefNames.Title],
                            state
                        };

                        workItemIdsFound.push(result.id);
                        deferreds[id].resolve(result);
                    });

                // Wait until all getWorkItemsPromises are done and
                // reject all ids that have not been found in getWorkItems;
                ids.forEach(id => {
                    if (workItemIdsFound.indexOf(id) === -1) {
                        console.warn(`WorkItem #${id} not returned from server.`);
                        // @ts-ignore TypeScript 4.1 upgrade
                        deferreds[id].resolve(undefined);
                    }
                });
            },
            (error: Error) => {
                Telemetry.traceError(this._context, "WorkItemProviderCache", error.message);
                ids.forEach(id => {
                    deferreds[id].reject(error);
                });
            }
        );

        return promises;
    }

    private _getProjectName(): string {
        const tfsPageService = this._context.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        return tfsPageData!.project!.name;
    }
}
